import { isProblemDetails, type ProblemDetails } from '@pm-tool/shared';
import type { z } from 'zod';

/** Non-2xx API response. `problem` is set when the body is RFC 9457 problem details. */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly problem: ProblemDetails | undefined;

  constructor(status: number, problem: ProblemDetails | undefined) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.status = status;
    this.problem = problem;
  }
}

type RequestOptions<S extends z.ZodType> = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Path below the API root, e.g. `/v1/projects`. */
  path: string;
  /** Validates the success response; the shared Zod schema is the API contract. */
  schema: S;
  /** JSON, or a Blob sent as is with its own type (profile pictures, ADM-07). */
  body?: unknown;
  signal?: AbortSignal;
};

const API_ROOT = '/api';

export async function apiRequest<S extends z.ZodType>({
  method = 'GET',
  path,
  schema,
  body,
  signal,
}: RequestOptions<S>): Promise<z.output<S>> {
  const raw = body instanceof Blob;
  const response = await fetch(`${API_ROOT}${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(body === undefined ? {} : { 'content-type': raw ? body.type : 'application/json' }),
    },
    ...(body === undefined ? {} : { body: raw ? body : JSON.stringify(body) }),
    ...(signal === undefined ? {} : { signal }),
  });

  const payload: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new ApiError(response.status, isProblemDetails(payload) ? payload : undefined);
  }
  return schema.parse(payload);
}

/**
 * Downloads a file answer (e.g. a CSV export) with the session cookie and saves it under the
 * server's file name. Errors arrive as ApiError like every other request.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`${API_ROOT}${path}`, { credentials: 'same-origin' });
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => undefined);
    throw new ApiError(response.status, isProblemDetails(payload) ? payload : undefined);
  }
  const name =
    /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ??
    fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}
